import { memo, useEffect } from 'react'

import { Input } from 'components'
import { TypesValidations } from 'constants/enums'
import statusRascunho from 'constants/statusRascunho'
import type { Color, Tamanho } from 'container/SalesOrder/interfaces'
import { Controller, FormProvider, useFormContext } from 'react-hook-form'
import { addSimulationAlteredMaterial } from 'store/SalesOrderContext/salesOrderSlice'
import { useDispatch } from 'store/store'

import * as S from './styles'

type ItemsSizeProps = {
  color: Color
  cd_matr: string
  quantConv: number
  isGrade: boolean
  materialStatus: string
  isCustom: boolean
  customBoxAmount: number
  pairsAmount: number
}

type SizeItemProps = {
  cd_tamh: string
  qt_item: number
  qt_caix: number
}

export const ItemsSize = ({
  color,
  cd_matr,
  isGrade,
  materialStatus,
  quantConv,
  isCustom,
  customBoxAmount,
  pairsAmount,
}: ItemsSizeProps) => {
  const methods = useFormContext()
  const { control, setValue, watch } = methods
  const boxAmount = watch('boxAmount')

  useEffect(() => {
    setValue(`boxAmount`, [customBoxAmount])
  }, [])

  return (
    <FormProvider {...methods}>
      <S.ContentSizes className="rodrigo">
        <div className="space-between">
          <h3>
            {color.cd_cor} - {color.ds_cor}
          </h3>
          <p id="subtotal">Subtotal: {pairsAmount} pares</p>
        </div>

        {isCustom && (
          <div className="box-amount">
            <h2>Edição de caixas</h2>
            <p>
              Abaixo, você pode editar, somente, o número total de caixas grades variáveis. Caso
              haja necessidade de edição da quantidade de produtos por caixa, volte à edição de
              pedidos.
            </p>
            <Controller
              control={control}
              name="boxAmount"
              render={({ field }) => (
                <Input
                  {...field}
                  maxLength={2}
                  onChange={(e) => {
                    field.onChange([parseInt(e.target.value ? e.target.value : '0')])
                  }}
                  width={78}
                />
              )}
            />

            <h2 id="sizes-label">Composição da caixa grade variável</h2>
          </div>
        )}

        {color.tamanhos.map((item, index) => (
          <S.SizesStyled className="column" key={`${color.cd_cor}-${item.cd_tamh}-caixas`}>
            {isCustom ? (
              <SizeItem cd_tamh={item.cd_tamh} qt_caix={boxAmount} qt_item={item.qt_item} />
            ) : (
              <MyInput
                cd_matr={cd_matr}
                colorName={color.ds_cor}
                materialStatus={materialStatus}
                quantConv={item.qt_item || 0}
                size={item}
                sizeIndex={index}
              />
            )}
          </S.SizesStyled>
        ))}
      </S.ContentSizes>
    </FormProvider>
  )
}

type MyInputProps = {
  size: Tamanho
  cd_matr: string
  sizeIndex: number
  quantConv: number
  colorName: string
  materialStatus: string
}

const MyInput = memo(function MyInput({
  colorName,
  cd_matr,
  sizeIndex,
  size,
  materialStatus,
}: MyInputProps) {
  const dispatch = useDispatch()
  const { control, setError, clearErrors } = useFormContext()
  useEffect(() => {
    if (
      size.fl_cota === TypesValidations.NO ||
      size.fl_estq === TypesValidations.NO ||
      materialStatus === statusRascunho.OUT_MINIMUM_ORDER ||
      materialStatus === statusRascunho.OUT_MINIMUM_ITEM
    ) {
      setError(`[${colorName}][${sizeIndex}]`, {
        message: 'error',
        type: 'validate',
      })
    } else {
      clearErrors(`[${colorName}][${sizeIndex}]`)
    }
  }, [size])

  const hasWarning = () => {
    const { WITHOUT_QUOTA, OUT_OF_STOCK } = statusRascunho
    const warningKinds = [WITHOUT_QUOTA, OUT_OF_STOCK]
    if (warningKinds.includes(materialStatus)) return true
    return false
  }

  return (
    <>
      <S.LabelStyled>{size.cd_tamh}</S.LabelStyled>
      <Controller
        control={control}
        defaultValue=""
        name={`[${colorName}][${sizeIndex}]`}
        render={({
          field: { name, onChange, value, onBlur, ref },
          fieldState: { invalid, error },
        }) => {
          const hasError = !!error?.message || invalid
          return (
            <>
              <Input
                errorOutline={hasError}
                hasWarning={hasError && hasWarning()}
                height={40}
                InputProps={{
                  readOnly: true,
                }}
                name={name}
                onBlur={onBlur}
                onChange={(e) => {
                  onChange(parseInt(e.target.value ? e.target.value : '0'))

                  dispatch(addSimulationAlteredMaterial(cd_matr))
                }}
                ref={ref}
                value={value || ''}
                width={66}
              />
            </>
          )
        }}
      />
    </>
  )
})

const SizeItem = ({ cd_tamh, qt_item, qt_caix }: SizeItemProps) => {
  return (
    <>
      <S.LabelStyled>{cd_tamh}</S.LabelStyled>
      <S.SizeContainer>
        <span>{qt_item}</span>
        <span>({qt_item * qt_caix})</span>
      </S.SizeContainer>
    </>
  )
}
